import React, { useEffect, useMemo, useState } from "react";
import api, { API, asArray, fileUrl, formatApiError } from "../../lib/api";
import FileUpload from "./FileUpload";
import XIcon from "../../components/XIcon";
import { SnapchatIcon, WhatsAppIcon } from "../../components/BrandSocialIcons";
import {
    AtSign,
    BriefcaseBusiness,
    Copy,
    Download,
    Edit3,
    Eye,
    Instagram,
    Linkedin,
    MapPin,
    Phone,
    Plus,
    QrCode,
    Save,
    Smartphone,
    Trash2,
    X,
} from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full rounded-lg border border-black/10 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-[#0060A7] focus:ring-4 focus:ring-[#0060A7]/10";
const labelCls = "mb-2 block text-[11px] font-bold uppercase tracking-[0.18em] text-[#6B7280]";

const blank = () => ({
    full_name: "",
    email: "",
    phone_number: "",
    mobile_number: "",
    extension_number: "",
    job_title: "",
    location: "",
    profile_picture: "",
    bio: "",
    show_bio: true,
    x_link: "",
    linkedin_link: "",
    instagram_link: "",
    snapchat_link: "",
    whatsapp_link: "",
    published: true,
});

const socialItems = (card) => [
    { url: card.location, Icon: MapPin, label: "Location" },
    { url: card.x_link, Icon: XIcon, label: "X" },
    { url: card.linkedin_link, Icon: Linkedin, label: "LinkedIn" },
    { url: card.instagram_link, Icon: Instagram, label: "Instagram" },
    { url: card.snapchat_link, Icon: SnapchatIcon, label: "Snapchat" },
    { url: card.whatsapp_link, Icon: WhatsAppIcon, label: "WhatsApp" },
].filter((item) => item.url);

function normalizeLink(value, type) {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (type === "whatsapp" && !/^https?:\/\//i.test(raw)) {
        const phone = raw.replace(/[^\d+]/g, "").replace(/^\+/, "");
        return phone ? `https://wa.me/${phone}` : raw;
    }
    if (/^https?:\/\//i.test(raw) || /^mailto:/i.test(raw) || /^tel:/i.test(raw)) return raw;
    return `https://${raw}`;
}

function qrDownloadName(card) {
    return `${(card.full_name || "business-card").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase()}-qr.png`;
}

export default function AdminBusinessCards() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            setItems(asArray((await api.get("/admin/business-cards")).data, "business_cards"));
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    useEffect(() => { load(); }, []);

    const stats = useMemo(() => ({
        total: items.length,
        published: items.filter((item) => item.published).length,
        hiddenBio: items.filter((item) => !item.show_bio).length,
    }), [items]);

    const save = async () => {
        setBusy(true);
        try {
            const payload = {
                ...editing,
                location: normalizeLink(editing.location),
                x_link: normalizeLink(editing.x_link),
                linkedin_link: normalizeLink(editing.linkedin_link),
                instagram_link: normalizeLink(editing.instagram_link),
                snapchat_link: normalizeLink(editing.snapchat_link),
                whatsapp_link: normalizeLink(editing.whatsapp_link, "whatsapp"),
            };
            if (payload.id) await api.patch(`/admin/business-cards/${payload.id}`, payload);
            else await api.post("/admin/business-cards", payload);
            toast.success("Business card saved");
            setEditing(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this business card?")) return;
        try {
            await api.delete(`/admin/business-cards/${id}`);
            toast.success("Business card deleted");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const copyPublicLink = async (card) => {
        const url = card.public_url || `${window.location.origin}/card/${card.slug}`;
        await navigator.clipboard.writeText(url);
        toast.success("Public link copied");
    };

    if (editing) {
        const e = editing;
        return (
            <div data-testid="admin-business-card-form" className="max-w-7xl">
                <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                    <button onClick={() => setEditing(null)} className="inline-flex items-center gap-2 rounded-lg border border-black/10 bg-white px-4 py-2 text-sm text-[#374151] shadow-sm hover:border-[#0060A7] hover:text-[#0060A7]">
                        <X size={15} /> Cancel
                    </button>
                    <button
                        onClick={save}
                        disabled={busy || !e.full_name || !e.email}
                        className="inline-flex items-center gap-2 rounded-lg bg-[#002951] px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-[#002951]/20 transition hover:bg-[#0060A7] disabled:opacity-50"
                    >
                        <Save size={15} /> {busy ? "Saving..." : "Save Card"}
                    </button>
                </div>

                <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_420px]">
                    <section className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-black/5 md:p-8">
                        <div className="mb-8">
                            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#0060A7]">Business Card</div>
                            <h1 className="heading mt-2 text-3xl tracking-tight">{e.id ? "Edit Digital Card" : "Create Digital Card"}</h1>
                        </div>

                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                            <Field label="Full name"><input className={inputCls} value={e.full_name} onChange={(ev) => setEditing({ ...e, full_name: ev.target.value })} required /></Field>
                            <Field label="Job title"><input className={inputCls} value={e.job_title} onChange={(ev) => setEditing({ ...e, job_title: ev.target.value })} /></Field>
                            <Field label="Email"><input type="email" className={inputCls} value={e.email} onChange={(ev) => setEditing({ ...e, email: ev.target.value })} required /></Field>
                            <Field label="Phone number"><input className={inputCls} value={e.phone_number} onChange={(ev) => setEditing({ ...e, phone_number: ev.target.value })} /></Field>
                            <Field label="Mobile number"><input className={inputCls} value={e.mobile_number} onChange={(ev) => setEditing({ ...e, mobile_number: ev.target.value })} /></Field>
                            <Field label="Extension"><input className={inputCls} value={e.extension_number} onChange={(ev) => setEditing({ ...e, extension_number: ev.target.value })} /></Field>
                            <Field label="Google Maps link"><input className={inputCls} value={e.location || ""} onChange={(ev) => setEditing({ ...e, location: ev.target.value })} placeholder="https://maps.google.com/..." /></Field>
                            <div className="md:col-span-2">
                                <FileUpload value={e.profile_picture} onChange={(v) => setEditing({ ...e, profile_picture: v })} label="Profile picture" testid="business-card-profile-upload" />
                            </div>
                            <div className="md:col-span-2">
                                <label className={labelCls}>Bio / description</label>
                                <textarea rows={4} className={inputCls} value={e.bio} onChange={(ev) => setEditing({ ...e, bio: ev.target.value })} />
                                <label className="mt-3 inline-flex items-center gap-2 text-sm text-[#374151]">
                                    <input type="checkbox" checked={e.show_bio} onChange={(ev) => setEditing({ ...e, show_bio: ev.target.checked })} />
                                    Show bio on public card
                                </label>
                            </div>
                            <Field label="X / Twitter link"><input className={inputCls} value={e.x_link} onChange={(ev) => setEditing({ ...e, x_link: ev.target.value })} placeholder="https://x.com/..." /></Field>
                            <Field label="LinkedIn link"><input className={inputCls} value={e.linkedin_link || ""} onChange={(ev) => setEditing({ ...e, linkedin_link: ev.target.value })} placeholder="https://linkedin.com/in/..." /></Field>
                            <Field label="Instagram link"><input className={inputCls} value={e.instagram_link} onChange={(ev) => setEditing({ ...e, instagram_link: ev.target.value })} placeholder="https://instagram.com/..." /></Field>
                            <Field label="Snapchat link"><input className={inputCls} value={e.snapchat_link} onChange={(ev) => setEditing({ ...e, snapchat_link: ev.target.value })} placeholder="https://snapchat.com/add/..." /></Field>
                            <Field label="WhatsApp number/link"><input className={inputCls} value={e.whatsapp_link} onChange={(ev) => setEditing({ ...e, whatsapp_link: ev.target.value })} placeholder="+966..." /></Field>
                            <div className="md:col-span-2">
                                <label className="inline-flex items-center gap-2 text-sm text-[#374151]">
                                    <input type="checkbox" checked={e.published} onChange={(ev) => setEditing({ ...e, published: ev.target.checked })} />
                                    Published and available on public URL
                                </label>
                            </div>
                        </div>
                    </section>

                    <aside className="space-y-4">
                        <BusinessCardPreview card={e} />
                        {e.qr_code_url && (
                            <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-black/5">
                                <div className="mb-3 flex items-center gap-2 text-sm font-bold text-[#002951]"><QrCode size={16} /> Generated QR Code</div>
                                <img src={e.qr_code_url} alt="Business card QR code" className="mx-auto h-44 w-44 rounded-lg border border-black/10 bg-white p-2" />
                                <div className="mt-4 flex gap-2">
                                    <a href={e.qr_code_url} download={qrDownloadName(e)} className="flex-1 rounded-lg bg-[#002951] px-3 py-2 text-center text-xs font-bold text-white">Download QR</a>
                                    <a href={`${API}/business-cards/${e.slug}/vcard`} className="flex-1 rounded-lg border border-black/10 px-3 py-2 text-center text-xs font-bold text-[#002951]">Download Contact</a>
                                </div>
                            </div>
                        )}
                    </aside>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="admin-business-cards-page" className="max-w-7xl">
            <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#0060A7]">Digital identity</div>
                    <h1 className="heading mt-2 text-3xl tracking-tight">Business Cards</h1>
                    <p className="mt-2 max-w-2xl text-sm text-[#6B7280]">Create public, scannable digital business cards with vCard downloads and QR codes.</p>
                </div>
                <button onClick={() => setEditing(blank())} className="inline-flex items-center gap-2 rounded-lg bg-[#002951] px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-[#002951]/20 transition hover:bg-[#0060A7]">
                    <Plus size={16} /> New Card
                </button>
            </div>

            <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-3">
                <Stat label="Total cards" value={stats.total} />
                <Stat label="Published" value={stats.published} />
                <Stat label="Bio hidden" value={stats.hiddenBio} />
            </div>

            {items.length === 0 ? (
                <div className="rounded-xl bg-white p-12 text-center shadow-sm ring-1 ring-black/5">
                    <QrCode className="mx-auto mb-4 text-[#0060A7]" size={34} />
                    <h2 className="heading text-xl">No business cards yet</h2>
                    <p className="mt-2 text-sm text-[#6B7280]">Add the first profile to generate its QR code and public contact page.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 2xl:grid-cols-3">
                    {items.map((card) => (
                        <article key={card.id} className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-black/5 transition hover:-translate-y-1 hover:shadow-xl hover:shadow-[#002951]/10">
                            <BusinessCardPreview card={card} compact />
                            <div className="grid grid-cols-2 gap-2 border-t border-black/5 p-4 sm:grid-cols-5">
                                <button onClick={() => setEditing(card)} className="inline-flex items-center justify-center gap-1 rounded-lg border border-black/10 px-3 py-2 text-xs font-bold text-[#002951] hover:border-[#0060A7]"><Edit3 size={13} /> Edit</button>
                                <a href={card.public_path} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-1 rounded-lg border border-black/10 px-3 py-2 text-xs font-bold text-[#002951] hover:border-[#0060A7]"><Eye size={13} /> View</a>
                                <button onClick={() => copyPublicLink(card)} className="inline-flex items-center justify-center gap-1 rounded-lg border border-black/10 px-3 py-2 text-xs font-bold text-[#002951] hover:border-[#0060A7]"><Copy size={13} /> Link</button>
                                <a href={card.qr_code_url} download={qrDownloadName(card)} className="inline-flex items-center justify-center gap-1 rounded-lg border border-black/10 px-3 py-2 text-xs font-bold text-[#002951] hover:border-[#0060A7]"><Download size={13} /> QR</a>
                                <button onClick={() => remove(card.id)} className="inline-flex items-center justify-center gap-1 rounded-lg border border-red-100 px-3 py-2 text-xs font-bold text-red-600 hover:bg-red-50"><Trash2 size={13} /> Delete</button>
                            </div>
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
}

function Field({ label, children }) {
    return <div><label className={labelCls}>{label}</label>{children}</div>;
}

function Stat({ label, value }) {
    return (
        <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-black/5">
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#6B7280]">{label}</div>
            <div className="heading mt-2 text-3xl text-[#002951]">{value}</div>
        </div>
    );
}

function BusinessCardPreview({ card, compact = false }) {
    const photo = card.profile_picture ? fileUrl(card.profile_picture) : null;
    const socials = socialItems(card);
    return (
        <div className={`relative overflow-hidden bg-[#002951] text-white ${compact ? "p-5" : "rounded-xl p-6 shadow-xl shadow-[#002951]/15"}`}>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_15%,rgba(43,176,212,0.28),transparent_34%),linear-gradient(140deg,#002951,#00213f)]" />
            <div className="relative">
                <div className="flex items-start gap-4">
                    <div className="h-20 w-20 shrink-0 overflow-hidden rounded-2xl border border-white/15 bg-white/10">
                        {photo ? <img src={photo} alt={card.full_name} className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-2xl font-bold">{(card.full_name || "?").slice(0, 1)}</div>}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="heading truncate text-xl">{card.full_name || "Full name"}</div>
                        <div className="mt-1 flex items-center gap-2 text-sm text-white/70"><BriefcaseBusiness size={14} /> <span className="truncate">{card.job_title || "Job title"}</span></div>
                        <div className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ${card.published ? "bg-emerald-400/15 text-emerald-100" : "bg-white/10 text-white/60"}`}>
                            {card.published ? "Published" : "Draft"}
                        </div>
                    </div>
                    {card.qr_code_url && <img src={card.qr_code_url} alt="" className="h-16 w-16 rounded-lg bg-white p-1" />}
                </div>
                {card.bio && card.show_bio && <p className="mt-4 line-clamp-3 text-sm leading-6 text-white/75">{card.bio}</p>}
                <div className="mt-5 grid gap-2 text-sm text-white/80">
                    {card.email && <Row Icon={AtSign} text={card.email} />}
                    {card.phone_number && <Row Icon={Phone} text={card.phone_number} />}
                    {card.mobile_number && <Row Icon={Smartphone} text={card.mobile_number} />}
                    {card.extension_number && <Row Icon={QrCode} text={`Ext. ${card.extension_number}`} />}
                </div>
                {socials.length > 0 && (
                    <div className="mt-5 flex flex-wrap gap-2">
                        {socials.map(({ url, Icon, label }) => (
                            <a key={label} href={normalizeLink(url, label.toLowerCase())} target="_blank" rel="noreferrer" aria-label={label} className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white hover:text-[#002951]">
                                <Icon size={16} />
                            </a>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function Row({ Icon, text }) {
    return <div className="flex min-w-0 items-center gap-2"><Icon size={14} className="shrink-0 text-[#81D3EB]" /><span className="truncate">{text}</span></div>;
}
